import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import useGetAgent from '@/hooks/useGetAgent';
import useGetProfile from '@/hooks/useGetProfile';
import { usePageSeo } from '@/hooks/usePageSeo';
import { cn } from '@/lib/utils';
import { useAuth0 } from '@auth0/auth0-react';
import {
  ArrowRight,
  Award,
  CheckCircle,
  FileText,
  Globe,
  HelpCircle,
  Mail,
  Phone,
  Search,
  Shield,
  Sparkles,
  TrendingDown,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { FaRegBuilding } from 'react-icons/fa6';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import Faq from './Faq';
import { GetHelpButton } from './GetHelpButton';
import LoadingModal from './LoadingModal';
import PropertyForm from './PropertyForm';
import ShareMenu from './ShareMenu';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';
import TutorialModal from './ui/TutorialModal';

const homepageFaqs = [
  {
    question: 'Why is my tax assessment higher than market value?',
    answer:
      "County appraisal districts often use mass appraisal techniques that don't account for specific property issues or recent market conditions. They may use outdated data or comparable properties that aren't truly similar to yours.",
  },
  {
    question: 'What happens after I appeal?',
    answer:
      "After filing an appeal, you'll typically receive a hearing date with your county's appraisal review board. You'll present evidence supporting your claim of a lower value. If successful, your assessed value may be reduced, resulting in lower property taxes.",
  },
  {
    question: 'Is there a deadline to protest property taxes?',
    answer:
      "Yes, most counties have strict deadlines for filing property tax appeals, typically 30 days after receiving your notice of appraised value. Missing this deadline usually means you'll have to wait until next year.",
  },
  {
    question: 'What evidence do I need to gather for my appeal?',
    answer:
      "Effective evidence includes recent comparable sales in your area, photos of property defects that may lower value, independent appraisals, repair estimates, and any documents showing factors that negatively impact your property's value.",
  },
  {
    question: 'Can I appeal my taxes every year?',
    answer:
      "Yes, you have the right to appeal your property tax assessment annually, regardless of previous appeal outcomes. Market conditions change, and your property's fair market value may fluctuate year to year.",
  },
];

const NewHome = () => {
  const navigate = useNavigate();
  const [query] = useSearchParams();

  const loanOfficer = query.get('loan-officer');
  const mortgage = query.get('mortgage');
  const userType = query.get('user_type');

  const agentIdentifier = useMemo(() => {
    const agent = query.get('agent');
    if (agent) window.localStorage.setItem('agent', agent.replace(' ', '+'));
    const localAgent = window.localStorage.getItem('agent');
    return localAgent;
  }, [query]);

  const { data: profile } = useGetProfile();
  const { isLoading, handleRedirectCallback, loginWithRedirect } = useAuth0();
  const location = useLocation();
  const { data: agent } = useGetAgent(profile?.agent ? null : agentIdentifier);

  const isDevelopmentPreview = location.pathname === '/new-home';

  usePageSeo({
    title: 'PropertyTaxHero: Lower Your Property Tax Bill — Free Appeal Help',
    description:
      'PropertyTaxHero helps homeowners appeal their property tax assessment for free. Find comparable sales, spot overassessment, and file a property tax appeal in minutes.',
    keywords:
      'PropertyTaxHero, lower your property tax bill, appeal your tax assessment, appeal property taxes, property tax appeal, property tax valuation appeal, property taxes, overassessment, property tax protest, reduce property taxes',
    path: isDevelopmentPreview ? '/new-home' : '/',
    noindex: isDevelopmentPreview,
  });
  const sponsoredLoanOfficer = useMemo(
    () =>
      agent?.loanOfficerId ?? {
        firstName: 'Rodden',
        lastName: 'Batson',
        phoneNumber: '720-404-9255',
        website: 'www.YourWayLoan.com',
        emailAddress: 'Rodden@YourWayLoan.com',
        photoUrl: '/rodden-batson.png',
        mortgageId: {
          name: 'Encompass Lending Group, LLC',
          mlsLicense: '292897',
          phoneNumber: '303-554-6886',
          website: 'https://encompasslending.com/',
          imageUrl: '/encompass.webp',
        },
      },
    [agent?.loanOfficerId],
  );

  useEffect(() => {
    if (loanOfficer) {
      window.localStorage.setItem('loan_officer_id', loanOfficer);
      window.localStorage.setItem('user_type', 'AGENT');
    }

    if (mortgage) {
      window.localStorage.setItem('mortgage_id', mortgage);
      window.localStorage.setItem('user_type', 'LOAN_OFFICER');
    }

    if (loanOfficer || mortgage) {
      loginWithRedirect({
        authorizationParams: {
          screen_hint: 'signup',
        },
        appState: {
          returnTo: '/dashboard?tab=profile',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanOfficer, mortgage]);

  useEffect(() => {
    if (loanOfficer)
      window.localStorage.setItem('loan_officer_id', loanOfficer);
  }, [loanOfficer]);

  useEffect(() => {
    if (mortgage) window.localStorage.setItem('mortgage_id', mortgage);
  }, [mortgage]);

  useEffect(() => {
    if (!isLoading && userType) {
      window.localStorage.setItem('user_type', userType ?? 'USER');

      if (['AGENT', 'LOAN_OFFICER'].includes(userType))
        loginWithRedirect({
          authorizationParams: {
            screen_hint: 'signup',
          },
          appState: {
            returnTo: '/dashboard?tab=reports',
          },
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, userType]);

  useEffect(() => {
    if (!isLoading && window.location.search.includes('code=')) {
      (async () => {
        const callback = await handleRedirectCallback();
        const returnTo = callback?.appState?.returnTo;

        if (returnTo && returnTo.startsWith('/')) {
          navigate(returnTo, { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  useEffect(() => {
    if (location.hash === '#property-form') {
      const element = document.getElementById('property-form-section');
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  if (isLoading)
    return (
      <div>
        <LoadingModal show />
      </div>
    );

  return (
    <div className="relative">
      {/* ── Sponsor bar ── */}
      {!!agent && (
        <div className="border-b border-border bg-card">
          <div className="container mx-auto px-4 py-3">
            <div
              className={cn(
                'grid gap-3 grid-cols-1',
                agent ? 'md:grid-cols-3' : 'md:grid-cols-2',
              )}
            >
              {/* Real Estate Agent - Courtesy Of */}
              {agent && (
                <HoverCard openDelay={200}>
                  <HoverCardTrigger asChild>
                    <div className="flex items-center gap-3 bg-background/60 p-3 rounded-xl border border-border/60 hover:border-primary/30 hover:shadow-md transition-all cursor-pointer group">
                      <Avatar className="h-10 w-10 flex-shrink-0 ring-2 ring-primary/20 group-hover:ring-primary/40 transition-all">
                        <AvatarImage
                          src={agent?.photoUrl}
                          alt={`${agent?.firstName} ${agent?.lastName}`}
                        />
                        <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                          {`${agent.firstName[0]}${agent.lastName[0]}`}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70 mb-0.5">
                          Courtesy of
                        </p>
                        <h3 className="font-semibold text-sm text-foreground truncate leading-tight">
                          {`${agent.firstName} ${agent.lastName}`}
                        </h3>
                        <p className="text-xs text-muted-foreground truncate">
                          {agent?.companyName}
                        </p>
                      </div>
                    </div>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-80 p-4" side="bottom">
                    <div className="flex gap-4">
                      <Avatar className="h-16 w-16 border-2 border-primary">
                        <AvatarImage
                          src={agent?.photoUrl}
                          alt={`${agent?.firstName} ${agent?.lastName}`}
                        />
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {`${agent.firstName[0]}${agent.lastName[0]}`}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 space-y-2">
                        <div>
                          <h4 className="font-semibold text-base">
                            {`${agent.firstName} ${agent.lastName}`}
                          </h4>
                          <p className="text-sm text-muted-foreground">
                            {agent?.companyName || 'PropertyTaxHero'}
                          </p>
                        </div>
                        <div className="space-y-1.5 text-sm">
                          {agent?.phoneNumber && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Phone className="h-3.5 w-3.5" />
                              <a
                                href={`tel:${agent?.phoneNumber}`}
                                className="hover:text-primary transition-colors"
                              >
                                {agent?.phoneNumber}
                              </a>
                            </div>
                          )}
                          {agent?.emailAddress && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Mail className="h-3.5 w-3.5" />
                              <a
                                href={`mailto:${agent?.emailAddress}`}
                                className="hover:text-primary transition-colors truncate max-w-[180px]"
                              >
                                {agent?.emailAddress}
                              </a>
                            </div>
                          )}
                          {agent?.website && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Globe className="h-3.5 w-3.5" />
                              <a
                                href={
                                  agent.website.startsWith('http')
                                    ? agent.website
                                    : `https://${agent.website}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hover:text-primary transition-colors"
                              >
                                {agent?.website}
                              </a>
                            </div>
                          )}
                          {agent?.licenseNumber && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Award className="h-3.5 w-3.5" />
                              <span>License #{agent?.licenseNumber}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </HoverCardContent>
                </HoverCard>
              )}

              {/* Mortgage Company - Sponsored By */}
              <HoverCard openDelay={200}>
                <HoverCardTrigger asChild>
                  <div className="flex items-center gap-3 bg-background/60 p-3 rounded-xl border border-border/60 hover:border-primary/30 hover:shadow-md transition-all cursor-pointer group">
                    <Avatar className="h-10 w-10 flex-shrink-0 ring-2 ring-primary/20 group-hover:ring-primary/40 transition-all">
                      <AvatarImage src={sponsoredLoanOfficer?.photoUrl} />
                      <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                        {`${sponsoredLoanOfficer?.firstName?.[0]}${sponsoredLoanOfficer?.lastName?.[0] ?? ''}`}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70 mb-0.5">
                        Sponsored by
                      </p>
                      <h3 className="font-semibold text-sm text-foreground truncate leading-tight">
                        {`${sponsoredLoanOfficer.firstName} ${sponsoredLoanOfficer.lastName}`}
                      </h3>
                      {sponsoredLoanOfficer.mortgageId?.name && (
                        <p className="text-xs text-muted-foreground truncate">
                          {sponsoredLoanOfficer.mortgageId?.name}
                        </p>
                      )}
                    </div>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent className="w-80 p-4" side="bottom">
                  <div className="flex gap-4">
                    <div className="h-16 w-16 flex-shrink-0 flex items-center justify-center border-2 border-primary rounded-md p-2">
                      {sponsoredLoanOfficer.mortgageId?.imageUrl ? (
                        <img
                          src={sponsoredLoanOfficer.mortgageId?.imageUrl}
                          alt={
                            sponsoredLoanOfficer.mortgageId?.name ||
                            'Mortgage Company'
                          }
                          className="max-h-full max-w-full object-contain"
                        />
                      ) : (
                        <FaRegBuilding className="h-6 w-6 text-primary" />
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <div>
                        <h4 className="font-semibold text-base">
                          {`${sponsoredLoanOfficer.firstName} ${sponsoredLoanOfficer.lastName}`}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {sponsoredLoanOfficer?.mortgageId.name}
                        </p>
                      </div>
                      <div className="space-y-1.5 text-sm">
                        {sponsoredLoanOfficer?.phoneNumber && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Phone className="h-3.5 w-3.5" />
                            <a
                              href={`tel:${sponsoredLoanOfficer?.phoneNumber}`}
                              className="hover:text-primary transition-colors"
                            >
                              {sponsoredLoanOfficer?.phoneNumber}
                            </a>
                          </div>
                        )}
                        {sponsoredLoanOfficer?.emailAddress && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Mail className="h-3.5 w-3.5" />
                            <a
                              href={`mailto:${sponsoredLoanOfficer?.emailAddress}`}
                              className="hover:text-primary transition-colors truncate max-w-[180px]"
                            >
                              {sponsoredLoanOfficer?.emailAddress}
                            </a>
                          </div>
                        )}
                        {sponsoredLoanOfficer?.website && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Globe className="h-3.5 w-3.5" />
                            <a
                              href={
                                sponsoredLoanOfficer.website.startsWith('http')
                                  ? sponsoredLoanOfficer?.website
                                  : `https://${sponsoredLoanOfficer.website}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:text-primary transition-colors"
                            >
                              {sponsoredLoanOfficer?.website}
                            </a>
                          </div>
                        )}
                        {sponsoredLoanOfficer?.mortgageId?.mlsLicense && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Award className="h-3.5 w-3.5" />
                            <span>
                              NMLS #
                              {sponsoredLoanOfficer?.mortgageId?.mlsLicense}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>

              {/* Company - Brought to you by */}
              <HoverCard openDelay={200}>
                <HoverCardTrigger asChild>
                  <div className="flex items-center gap-3 bg-background/60 p-3 rounded-xl border border-border/60 hover:border-primary/30 hover:shadow-md transition-all cursor-pointer group">
                    <div className="h-10 w-10 flex-shrink-0 flex items-center justify-center">
                      <img
                        src="/mortgage-news-logo-icon.png"
                        alt="MortgageNews.org"
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70 mb-0.5">
                        Brought to you by
                      </p>
                      <h3 className="font-semibold text-sm text-foreground leading-tight">
                        MortgageNews.org
                      </h3>
                      <a
                        href="https://www.mortgagenews.org"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1 mt-0.5"
                      >
                        <Globe className="h-2.5 w-2.5" />
                        <span className="truncate">mortgagenews.org</span>
                      </a>
                    </div>
                  </div>
                </HoverCardTrigger>
                <HoverCardContent className="w-80 p-4" side="bottom">
                  <div className="flex gap-4">
                    <div className="h-16 w-16 flex-shrink-0 flex items-center justify-center border-2 border-primary rounded-md p-2">
                      <img
                        src="/mortgage-news-logo-icon.png"
                        alt="MortgageNews.org"
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                    <div className="flex-1 space-y-2">
                      <div>
                        <h4 className="font-semibold text-base">
                          MortgageNews.org
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          Your Source for Mortgage Industry News
                        </p>
                      </div>
                      <div className="space-y-1.5 text-sm">
                        <p className="text-muted-foreground text-xs leading-relaxed">
                          The latest news, trends, and insights in the mortgage
                          and real estate industry.
                        </p>
                        <div className="flex items-center gap-2 text-muted-foreground pt-1">
                          <Globe className="h-3.5 w-3.5" />
                          <a
                            href="https://www.mortgagenews.org"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:text-primary transition-colors font-medium"
                          >
                            Visit mortgagenews.org
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>
          </div>
        </div>
      )}

      {/* ── Hero ── */}
      <section className="relative overflow-hidden">
        {/* Subtle background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background to-muted/30 pointer-events-none" />
        <div className="container mx-auto px-4 pt-16 pb-8 md:pt-24 md:pb-12 relative">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-bold tracking-tight text-foreground text-4xl md:text-[56px] md:leading-[1.08] mb-6">
              Overpaying Property Taxes?{' '}
              <span className="text-primary">Fix It in Minutes.</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed mb-10 max-w-2xl mx-auto">
              PropertyTaxHero combines real market comps, an interactive value
              model, and AI guidance—so your appeal is backed by data, not
              assumptions. Completely free, no surprises.
            </p>

            {/* Trust pills */}
            <div className="flex flex-wrap justify-center gap-3 mb-6">
              {[
                { icon: CheckCircle, text: '100% free — really' },
                { icon: CheckCircle, text: 'Live MLS comps' },
                { icon: CheckCircle, text: 'Appeal-ready reports' },
              ].map((item, i) => (
                <div
                  key={i}
                  className="flex items-center gap-2 bg-card border border-border rounded-full px-4 py-2 text-sm font-medium text-foreground shadow-sm"
                >
                  <item.icon className="w-4 h-4 text-primary" />
                  {item.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── How it works ── */}
      <section className="bg-muted/40">
        <div className="container mx-auto px-4 py-12 md:py-16">
          <div className="max-w-4xl mx-auto">
            <p className="text-center text-xs font-bold uppercase tracking-widest text-primary mb-3">
              How it works
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-center text-foreground mb-12">
              Three Steps. One Lower Tax Bill.
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  n: '1',
                  icon: Search,
                  title: 'Drop Your Address',
                  desc: "Type it in — we'll pull your property details, tax history, and assessment data in seconds.",
                },
                {
                  n: '2',
                  icon: Sparkles,
                  title: 'Make Your Case',
                  desc: 'We find the strongest comparable sales so you can back up your value with real data.',
                },
                {
                  n: '3',
                  icon: FileText,
                  title: 'File Like a Pro',
                  desc: 'Get a polished appeal letter, evidence packet, and step-by-step filing instructions for your county.',
                },
              ].map((step) => (
                <div
                  key={step.n}
                  className="bg-card p-8 rounded-2xl shadow-sm border border-border hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                >
                  <div className="h-12 w-12 bg-primary rounded-xl flex items-center justify-center mb-5">
                    <step.icon className="h-6 w-6 text-primary-foreground" />
                  </div>
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-xs font-bold text-primary uppercase tracking-wider">
                      Step {step.n}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Property form ── */}
      <section className="container mx-auto px-4 py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          <div id="property-form-section" className="scroll-mt-24">
            <div className="space-y-6">
              <div className="flex flex-col md:flex-row items-center justify-between space-y-3 md:space-y-0">
                <div className="w-[108px] hidden md:block" />
                <div className="flex items-center justify-center gap-3">
                  <div className="h-9 w-9 bg-primary rounded-xl flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-foreground font-bold text-sm">
                      1
                    </span>
                  </div>
                  <h3 className="text-lg md:text-xl font-bold text-foreground uppercase tracking-wide">
                    Start Here — Enter Your Address
                  </h3>
                </div>
                <GetHelpButton />
              </div>
              <PropertyForm />
            </div>
          </div>
        </div>
      </section>

      {/* ── Why homeowners use PTH ── */}
      <section className="bg-muted/40">
        <div className="container mx-auto px-4 py-12 md:py-16">
          <div className="max-w-4xl mx-auto">
            <p className="text-center text-xs font-bold uppercase tracking-widest text-primary mb-3">
              Why PropertyTaxHero
            </p>
            <h2 className="text-2xl md:text-3xl font-bold text-center text-foreground mb-4">
              Because Overpaying Is Optional
            </h2>
            <p className="text-base md:text-lg text-center text-muted-foreground mb-12 max-w-2xl mx-auto leading-relaxed">
              Counties make mistakes. We give you the tools to catch them — and
              the reports to prove it. Completely free, forever.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  icon: FileText,
                  title: 'Counties Mess Up. A Lot.',
                  desc: 'Mass appraisal is a blunt instrument. Important details about your property get missed, and you end up overpaying.',
                },
                {
                  icon: TrendingDown,
                  title: 'One Win Pays Off for Years',
                  desc: "A successful appeal doesn't just save you money this year — it can reset your baseline for future assessments too.",
                },
                {
                  icon: Shield,
                  title: 'Free Means Free',
                  desc: 'No credit card. No trial. No catch. We never charge homeowners and we never sell your data. Period.',
                },
                {
                  icon: CheckCircle,
                  title: 'Every State. Every County.',
                  desc: null,
                  customDesc: true,
                },
              ].map((card, i) => (
                <div
                  key={i}
                  className="bg-card p-7 rounded-2xl shadow-sm border border-border hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
                >
                  <div className="flex items-start gap-4">
                    <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <card.icon className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-foreground mb-1.5">
                        {card.title}
                      </h3>
                      {card.customDesc ? (
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          From{' '}
                          <Link
                            to="/texas"
                            className="text-primary hover:underline font-medium"
                          >
                            Texas
                          </Link>{' '}
                          to{' '}
                          <Link
                            to="/california"
                            className="text-primary hover:underline font-medium"
                          >
                            California
                          </Link>
                          ,{' '}
                          <Link
                            to="/florida"
                            className="text-primary hover:underline font-medium"
                          >
                            Florida
                          </Link>{' '}
                          to{' '}
                          <Link
                            to="/new-york"
                            className="text-primary hover:underline font-medium"
                          >
                            New York
                          </Link>{' '}
                          — we provide appeal guides, deadlines, and comparable
                          sales data for every state.
                        </p>
                      ) : (
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {card.desc}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Resource links ── */}
      <section className="container mx-auto px-4 py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          <p className="text-center text-xs font-bold uppercase tracking-widest text-primary mb-3">
            Dig Deeper
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-center text-foreground mb-10">
            Level Up Your Appeal Game
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { to: '/how-to-appeal-property-tax', label: 'How to Appeal' },
              { to: '/lower-property-tax-bill', label: 'Lower Your Tax Bill' },
              { to: '/property-tax-protest', label: 'Protest Guide' },
              {
                to: '/property-tax-comparable-sales-guide',
                label: 'Comparable Sales',
              },
              {
                to: '/property-tax-appeal-deadlines',
                label: 'Appeal Deadlines',
              },
              { to: '/all-states', label: 'All 50 State Guides' },
            ].map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="group flex items-center justify-between p-4 bg-card border border-border rounded-xl hover:shadow-lg hover:border-primary/30 hover:-translate-y-0.5 transition-all duration-300 text-foreground font-medium text-sm"
              >
                <span>{link.label}</span>
                <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="bg-muted/40">
        <div className="container mx-auto px-4 py-16 md:py-20">
          <div className="max-w-3xl mx-auto">
            <Faq />
            <div className="mt-10 text-center">
              <Button
                asChild
                className="rounded-xl px-8 h-12 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
              >
                <Link to="/faq">View All FAQs</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
export default NewHome;
